import { InjectionToken, inject } from '@angular/core';
import { HandleRequestService, createApiActions } from '@essent/common';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { from, map, catchError, of, firstValueFrom } from 'rxjs';
import { HttpBaseClient } from '@integration/api-client/client-core';

/**
 * Injection token for a request client that will be used by the ngrx service wrapper.
 * This needs to be provided for the service to work correctly.
 */
const RequestClientInjectionToken = new InjectionToken('RequestClient');

/**
 * To accomodate the noCache functionality, we need to provide extra information to the client when making the request.
 * This function converts the noCache parameter to headers that can be used in the request.
 */
const paramsToHeaders = (params) => ({
    ...(params?.noCache ? { 'Cache-Control': 'max-age=0' } : undefined)
});
/**
 * Factory for a simple effect that can be triggered by a request action, and returns an effect that will dispatch the success or error action
 * @param httpCall Http call that contains mapping and the endpoint
 * @param effectConfiguration Actions to use
 * @param requestClient A request client. If not provided, client will be injected using the `RequestClientInjectionToken`.
 * Note that one of these methods **needs** to be used.
 * @returns effect for this http call
 */
const effectsFactory = (httpCall, effectConfiguration, requestClient) => {
    return createEffect((requestService = inject(HandleRequestService), actions$ = inject(Actions), client = requestClient ?? inject(RequestClientInjectionToken)) => requestService.handle(actions$.pipe(ofType(effectConfiguration.requestAction)), ({ type, payload, ...params }) => from(httpCall(payload, client, paramsToHeaders(params))).pipe(map(succesPayload => effectConfiguration.successAction({
        actionId: params.actionId,
        payload: succesPayload
    })), catchError((errorPayload) => of(effectConfiguration.errorAction({
        actionId: params.actionId,
        payload: errorPayload
    }))))), {
        functional: true
    });
};
const effectsAndActionsFactory = (httpCall, actionPrefix, requestClient) => {
    const actions = createApiActions(actionPrefix);
    const effect = effectsFactory(httpCall, actions, requestClient);
    return { actions, effect };
};
/**
 * Extracts effects and actions from a service object and returns them in a format that can be used in ngrx
 * @returns an object with actions and effects, named per operation
 *
 * ## Example usage
 * ```ts
 * import { generateNgrxServiceClient } from '@cockpit/core-modules/ngrx-service-wrapper';
 * import { becomeACustomerClient } from '@integration/api-client/become-a-customer-client';
 *
 * export const { actions, effects } = generateNgrxServiceClient('Become a customer', becomeACustomerClient);
 * ```
 */
const generateNgrxServiceClient = (serviceName, service, requestClient) => {
    const serviceMethodNames = Object.keys(service);
    return serviceMethodNames.reduce((prev, methodName) => {
        const { actions, effect } = effectsAndActionsFactory(service[methodName], `[${serviceName}] ${methodName}`, requestClient);
        return {
            actions: {
                ...prev.actions,
                [methodName]: actions
            },
            effects: {
                ...prev.effects,
                [`${methodName}Effect`]: effect
            }
        };
    }, {
        effects: {},
        actions: {}
    });
};

/**
 * Simple wrapper for the HTTP client to be used by the RequestClient.
 */
const httpRequestFactory = (httpClient) => async (url, options) => firstValueFrom(httpClient.request(options.method, url, {
    body: options.body,
    headers: options.headers
}));
/**
 * Configures the default HttpBaseClient with the provided HTTP client.
 * This method should be called once during the application initialization.
 */
const getRequestClient = (httpClient, baseUrl = '') => {
    return new HttpBaseClient(baseUrl, httpRequestFactory(httpClient));
};

/**
 * Generated bundle index. Do not edit.
 */

export { RequestClientInjectionToken, effectsAndActionsFactory, effectsFactory, generateNgrxServiceClient, getRequestClient, httpRequestFactory, paramsToHeaders };
